import { LatLng } from "../../../../model/LatLng";
import { DecodePath } from "../../../../tools/ItineraryDecoder";
import {
    hasNonEmptyName,
    isValidCoordinate,
    nearestPointOnPolyline,
} from "../../../../tools/MapItineraryTools";
import { formatStopNameShort } from "../../../../tools/stopNameFormatter";
import { normalizeStopLabel } from "../../../../tools/StringTools";
import type { ItineraryLegDTO, ItineraryLegStepDTO } from "../../../../types/dtos/api/itinerary/itinerary.dto";
import {
    applyActiveStopDotPresentation,
    buildStopLabelLine1,
    createStopLabelDot,
    createStopLabelElement,
} from "../stopLabels";
import type { TransitPathStopCollisionEntry } from "../stopOverlay";
import { TRANSIT_PATH_STOP_LABEL_COLLISION_PRIORITY } from "../transitPath.configuration";
import { buildItineraryStopLabelData } from "./itineraryTransitPath.labelData.tools";

/**
 * Builds collision entries for the selected itinerary transit leg (endpoints + intermediates).
 *
 * @param selectedLeg Selected transit leg.
 * @param legColorHex Resolved leg color hex.
 * @param isDarkUi Whether dark UI is active.
 * @returns Collision entries for Effect B.
 */
export function buildItineraryTransitPathStopCollisionEntries(
    selectedLeg: ItineraryLegDTO,
    legColorHex: string,
    isDarkUi: boolean,
): TransitPathStopCollisionEntry[] {
    const stopLabelEntries: TransitPathStopCollisionEntry[] = [];

    const pushEndpointLabelIfValid = ( step: ItineraryLegStepDTO | undefined, role: "from" | "to" ): void => {
        if ( step === undefined ) {
            return;
        }
        const rawStepName = normalizeStopLabel( step.name );
        const normalizedStepName = rawStepName !== undefined ? formatStopNameShort( rawStepName ) : undefined;
        if ( !hasNonEmptyName( normalizedStepName ) ) {
            return;
        }
        if ( !isValidCoordinate( step.location.lat, step.location.lon ) ) {
            return;
        }
        const stopData = buildItineraryStopLabelData( step, normalizedStepName );
        const dotEl = createStopLabelDot( legColorHex );
        applyActiveStopDotPresentation( dotEl, isDarkUi );

        const { element, config } = createStopLabelElement(
            {
                "variant":  "endpointChip",
                legColorHex,
                isDarkUi,
                "aboveDot": true,
            },
            stopData,
            buildStopLabelLine1( stopData ),
        );

        element.className += ` ${ config.className }`;

        stopLabelEntries.push( {
            "id":            `${ selectedLeg.id }-${ role }`,
            "lngLat":        [ step.location.lon, step.location.lat ],
            "priority":      TRANSIT_PATH_STOP_LABEL_COLLISION_PRIORITY.highlight,
            "dotElement":    dotEl,
            "labelElement":  element,
            "labelConfig":   config,
            "lockPlacement": true,
        } );
    };

    const legPathForSnap =
        selectedLeg.geometry?.path !== undefined && selectedLeg.geometry.path.length > 0
            ? DecodePath(
                selectedLeg.geometry.path,
                selectedLeg.geometry.type,
                selectedLeg.geometry.precision,
            )
            : [];

    if ( selectedLeg.intermediateStops !== undefined ) {
        selectedLeg.intermediateStops.forEach( ( stop, stopIndex ) => {
            if ( !isValidCoordinate( stop.location.lat, stop.location.lon ) ) {
                return;
            }

            const rawStopName = normalizeStopLabel( stop.name );
            const normalizedStopName = rawStopName !== undefined ? formatStopNameShort( rawStopName ) : undefined;
            const nameLabel = hasNonEmptyName( normalizedStopName ) ? normalizedStopName : null;

            const stopAsLatLng = new LatLng( stop.location.lat, stop.location.lon );
            const onLeg =
                legPathForSnap.length >= 2
                    ? nearestPointOnPolyline( stopAsLatLng, legPathForSnap )
                    : stopAsLatLng;

            const dotEl = createStopLabelDot( legColorHex );
            applyActiveStopDotPresentation( dotEl, isDarkUi );

            const entry: TransitPathStopCollisionEntry = {
                "id":         `${ selectedLeg.id }-intermediate-${ stopIndex }`,
                "lngLat":     [ onLeg.lng, onLeg.lat ],
                "priority":   TRANSIT_PATH_STOP_LABEL_COLLISION_PRIORITY.intermediate,
                "dotElement": dotEl,
            };

            if ( nameLabel !== null ) {
                const stopData = buildItineraryStopLabelData( stop, nameLabel );
                const line1 = buildStopLabelLine1( stopData );
                const { element, config } = createStopLabelElement(
                    {
                        "variant": "bubble",
                        legColorHex,
                        isDarkUi,
                    },
                    stopData,
                    line1,
                );
                element.className += ` ${ config.className }`;
                entry.labelElement = element;
                entry.labelConfig = config;
            }

            stopLabelEntries.push( entry );
        } );
    }

    pushEndpointLabelIfValid( selectedLeg.from, "from" );
    pushEndpointLabelIfValid( selectedLeg.to, "to" );

    return stopLabelEntries;
}
